<template>
  <div class="module-content">
    <!-- Header -->
    <div class="module-header">
      <div class="left">
        <h2>All Users</h2>
      </div>

      <div class="right">
        <!-- Search -->
        <SearchInput :items="items" :attributes="tableAttributes" :entityName="'users'" @refresh="filterItems = $event">
        </SearchInput>

        <!-- Add new user -->
        <button class="action" title="Add new user" @click="showCreateUser()" v-if="canAdd" select-cy="create">
          <i class="icn icn-plus"></i>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="container w-max">
      <div class="flex flex-col items-center">
        <img class="h-36 mt-20" src="@/assets/img/loading.svg" />
        <h2>Fetching users</h2>
      </div>
    </div>

    <!-- List of the users -->
    <div v-if="!isLoading" class="container">
      <div class="table-wrapper">
        <Table
          :items="filterItems"
          :attributes="tableAttributes"
          :options="tableOptions"
          @edit="editItem"
          @delete="deleteItem"
        ></Table>
      </div>
    </div>

    <UserCreate v-if="isCreateOpen"></UserCreate>
  </div>
</template>

<script>
import SearchInput from '@/components/SearchInput.vue';
import Table from '@/components/Table.vue';
import UserCreate from './UserCreate.vue';
import { MODAL_NAME } from './../store';
import { CONFIRM_COLOR, MESSAGES } from '@/helpers/constants';
import { PERMISSION, MODULE } from '@/helpers/security';

export default {
  name: 'UsersList',
  components: {
    UserCreate,
    SearchInput,
    Table,
  },

  data() {
    return {
      isLoading: false,
      tableOptions: {
        edit: true,
        delete: false,
        editLabel: 'View',
      },
      tableAttributes: [
        { name: 'firstName', label: 'First Name' },
        { name: 'lastName', label: 'Last Name' },
        { name: 'email', label: 'Email' },
        { name: 'company.name', label: 'Company' },
        { name: 'creationDate', label: 'Joined', type: 'date' },
        {
          name: 'isEnabled',
          label: 'Enabled',
          type: 'badge',
          config: {
            true: { color: 'green', text: 'Yes' },
            false: { color: 'red', text: 'No' },
          },
        },
      ],
      superAdminColumn: {
        name: 'isSuperAdmin',
        label: 'Super Admin',
        type: 'badge',
        config: {
          true: { color: 'green', text: 'Yes' },
          false: { color: 'red', text: 'No' },
        },
      },
      filterItems: [],
    };
  },
  computed: {
    isCreateOpen() {
      return this.$store.getters['users/isCreateOpen'];
    },
    items() {
      return this.$store.getters['users/getUsers'];
    },
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    isSuperAdmin() {
      return this.$store.getters['auth/isSuperAdmin'];
    },
    canAdd() {
      return this.$store.getters['auth/hasAccess'](MODULE.USER, PERMISSION.CREATE);
    },
    canDelete() {
      return this.$store.getters['auth/hasAccess'](MODULE.USER, PERMISSION.DELETE);
    },
  },
  watch: {
    isCreateOpen(newVal) {
      if (newVal === false) {
        this.fetchUsers();
      }
    },
    canDelete() {
      this.tableOptions.delete = this.canDelete;
    },
  },

  mounted() {
    this.fetchUsers();
    this.tableOptions.delete = this.canDelete;

    if (this.isSuperAdmin) {
      this.tableAttributes.push(this.superAdminColumn);
    }
  },

  methods: {
    fetchUsers() {
      this.isLoading = true;
      this.$store
        .dispatch('users/get', { sort: 'firstName', limit: 200 })
        .then(({ data }) => {
          this.$store.commit('users/itemsLoaded', data);
          this.filterItems = data;
        })
        .catch((err) => {
          console.error(err);
          let msg = 'Please try again later or contact an administrator.';
          if (err && err.status && err.status === 401) {
            msg = 'You cannot list the items. Please try again later or contact an administrator.';
          }
          this.$store.dispatch('notifications/error', {
            headline: MESSAGES.ERROR_FETCH,
            details: msg,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    editItem(item) {
      this.$router.push(`users/${item.id}`);
    },
    deleteItem(item) {
      if (this.currentUser.id === item.id) {
        this.$store.dispatch('notifications/error', { headline: 'You cannot delete yourself.' });
      } else {
        this.$store
          .dispatch('confirm/confirm', {
            color: CONFIRM_COLOR.DANGER,
            details: `Would you like to delete ${item.firstName} ${item.lastName}? This action is irreversible.`,
          })
          .then((res) => {
            if (res) {
              this.isLoading = true;
              this.$store
                .dispatch('users/delete', item.id)
                .then(() => {
                  this.$store.dispatch('notifications/success', { headline: MESSAGES.SUCCESSFUL_DELETE });
                })
                .catch((err) => {
                  console.error(err);
                  this.$store.dispatch('notifications/error', { headline: MESSAGES.NO_PERMISSION });
                })
                .finally(() => {
                  this.fetchUsers();
                });
            }
          });
      }
    },
    showCreateUser() {
      if (!this.isCreateOpen) {
        this.$store.commit('users/open', MODAL_NAME.CREATE);
      }
    },
  },
};
</script>